'use client';

import {
  DataGridPro,
  GridColDef,
  GridColumnGroupingModel,
  GridColumnVisibilityModel,
  GridDensity,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';

import { useEffect, useMemo, useState, FC, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import Link from 'next/link';
import { useBackend } from '@/hooks/useBackend';
import {
  useProjectFilterStore,
  loadStoredColumnVisibility,
  COLUMN_VISIBILITY_STORAGE_KEY,
} from '@/stores/projectFilterStore';
import { scopedLocalStorage } from '@/lib/browser/scopedStorage';

// Storage key for sort model
const PROJECT_SORT_MODEL_STORAGE_KEY = 'projectGridSortModel';

// Helper function to load sort model from localStorage
const loadStoredProjectSortModel = (): GridSortModel => {
  try {
    const stored = scopedLocalStorage.getItem(PROJECT_SORT_MODEL_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that it's an array and has the expected structure
      if (
        Array.isArray(parsed) &&
        parsed.every(
          (item) =>
            typeof item === 'object' && typeof item.field === 'string' && (item.sort === 'asc' || item.sort === 'desc')
        )
      ) {
        return parsed;
      }
    }
  } catch (error) {
    log.warn('Failed to load stored project sort model:', error);
  }
  // Return default sort model
  return [{ field: 'projectLabel', sort: 'asc' }];
};
import { log } from '@/lib/utils/logger';
import { dataGridStyle } from '@/styles/tableStyles';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { ProjectGridToolbar } from '@/components/tables/ProjectGridToolbar';
import { enforceProjectGridNonHideableColumns } from '@/lib/constants/projectGridColumnGroups';
import { useUserResolution } from '@/lib/options/useUserResolution';
import type { ProjectSearchCriteria, ProjectSearchStatusDto } from '@/api/ApiClient';
import { removeLeadingTrailingBrTags } from '@/lib/utils/textUtils';
import { useGridSearchErrorHandler } from '@/hooks/useGridSearchErrorHandler';
import { consumeNextSearchUserInitiated } from '@/stores/searchUiStore';
import { formatUsJurisdictionForDisplay } from '@/lib/constants/usJurisdictionOptions';
import { ProjectSearchNoRowsOverlay } from '@/components/tables/ProjectSearchNoRowsOverlay';
import { usePrivileges } from '@/hooks/usePrivileges';
import { PrivilegeEnum } from '@/api/ApiClient';
import type { Operation } from '@/stores/authStore';

/** Renders sample/stat counts when the API omits values (no privilege or not applicable). */
function formatNullableCount(value: number | null | undefined): string | number {
  if (value === null || value === undefined) return '—';
  return value;
}

// Helper functions moved outside component
const getProjectStatusLabel = (key: string, projectSearchStatus: any[]) => {
  return projectSearchStatus?.find((entry: any) => entry.key === key)?.value || key;
};

const getProjectTypeLabel = (type: any, projectTypes: any[]) => {
  // Handle ProjectTypeDto object (new structure)
  if (type && typeof type === 'object' && type.label) {
    return type.label;
  }
  // Handle string code (legacy or fallback)
  if (typeof type === 'string') {
    return projectTypes?.find((entry: any) => entry.code === type || entry.id?.toString() === type)?.label || type;
  }
  return '-';
};

const getTechniqueName = (id: number | string, synthesisTechniques: any[]) => {
  const idStr = id.toString();
  return synthesisTechniques?.find((entry: any) => entry.id === idStr)?.name || idStr;
};

/** Resolves configured materials: API may return names (internal search) or ids (legacy). */
function formatProjectMaterialsCell(
  project: { materials?: string[]; materialIds?: number[] },
  materialMap: Map<number, string>
): string {
  const mats = project.materials;
  const ids = project.materialIds;
  if (Array.isArray(mats) && mats.length > 0) {
    const first = mats[0];
    if (typeof first === 'string' && Number.isNaN(Number(first))) {
      return mats.filter(Boolean).join(', ') || '-';
    }
    if (materialMap.size > 0) {
      const resolved = mats.map((id) => materialMap.get(Number(id))).filter(Boolean) as string[];
      if (resolved.length) return resolved.join(', ');
    }
  }
  if (Array.isArray(ids) && ids.length > 0 && materialMap.size > 0) {
    const resolved = ids.map((id) => materialMap.get(Number(id))).filter(Boolean) as string[];
    if (resolved.length) return resolved.join(', ');
  }
  return '-';
}

function formatProjectTechniquesCell(project: { techniques?: string[] }, synthesisTechniques: any[]): string {
  const tech = project.techniques;
  if (!Array.isArray(tech) || tech.length === 0) return '-';
  return (
    tech
      .map((id) => getTechniqueName(id, synthesisTechniques))
      .filter(Boolean)
      .join(', ') || '-'
  );
}

/**
 * Builds comma-separated collaborator names for faculty/staff grid cells.
 * The API returns one Collaborators row per project role slot, so the same person (same id)
 * can appear twice when they hold multiple roles — `faculty`/`staff` ID lists match both rows,
 * which would duplicate their name unless we collapse by collaborator id here.
 */
function formatCollaboratorNamesForRoleColumns(
  collaborators: { id?: string; firstName?: string; lastName?: string }[] | undefined,
  roleMemberIds: string[] | undefined
): string {
  if (!collaborators?.length || !roleMemberIds?.length) return '-';
  const allowed = new Set(roleMemberIds.filter(Boolean));
  const seenIds = new Set<string>();
  const names: string[] = [];
  for (const c of collaborators) {
    const id = c.id;
    if (!id || !allowed.has(id) || seenIds.has(id)) continue;
    seenIds.add(id);
    const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    if (name) names.push(name);
  }
  return names.length ? names.join(', ') : '-';
}

export type ProjectRowType = {
  id: number;
  projectLabel: string;
  type: string;
  title?: string;
  startDate?: string;
  endDate?: string;
  projectStatus: string;
  user?: string;
  institutionType?: string;
  piInstitution?: string;
  /** Raw PI state code from the API (resolved for display via formatUsPiJurisdictionForDisplay). */
  piState?: string;
  faculty?: string;
  staff?: string;
  totalNbSamplesRequested?: number | null;
  nbSamplesShipped?: number | null;
  totalNbSamples?: number | null;
  nbSamplesDue?: number | null;
  lastChangedDate?: string;
  lastChangedBy?: string;
  materials?: string;
  techniques?: string;
};

interface ProjectGridCoreProps {
  /** Whether to show links in projectLabel column */
  showLinks?: boolean;
  /** Whether to enable row selection */
  enableSelection?: boolean;
  /** Selection model for controlled selection */
  selectionModel?: GridRowSelectionModel;
  /** Callback for selection changes */
  onSelectionChange?: (selection: GridRowSelectionModel) => void;
  /** Whether to show checkboxes for multiple selection */
  checkboxSelection?: boolean;
  /** Custom toolbar slot */
  toolbarSlot?: React.ComponentType<any>;
  /** Custom columns to override defaults */
  customColumns?: GridColDef[];
  /** Whether to disable column menu */
  disableColumnMenu?: boolean;
  /** Whether to show the toolbar (columns, density, export) */
  showToolbar?: boolean;
  /** Grid density setting */
  density?: GridDensity;
  /** Callback when a project is selected (with full project data) */
  onProjectSelect?: (project: ProjectRowType) => void;
  /** Callback when multiple projects are selected (with full project data) */
  onProjectsSelect?: (projects: ProjectRowType[]) => void;
  /** Options data - passed from parent to avoid useSelectOptions conflicts */
  optionsData?: {
    projectStatus?: any[];
    projectType?: any[];
    synthesisTechniques?: any[];
    materials?: any[];
  };
  /** Whether options are loading */
  optionsLoading?: boolean;
  /** Optional filters to override store filters (useful for selection dialogs) */
  filters?: ProjectSearchCriteria;
  /** Optional pagination model to override store pagination (useful for selection dialogs) */
  paginationModel?: { page: number; pageSize: number };
  /** Callback to update pagination when using local state */
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
}

export const ProjectGridCore: FC<ProjectGridCoreProps> = ({
  showLinks = true,
  enableSelection = false,
  selectionModel,
  onSelectionChange,
  checkboxSelection = false,
  toolbarSlot,
  customColumns,
  disableColumnMenu = false,
  showToolbar = true,
  density: propDensity,
  onProjectSelect,
  onProjectsSelect,
  optionsData,
  optionsLoading = false,
  filters: propFilters,
  paginationModel: propPaginationModel,
  onPaginationModelChange,
}) => {
  const backend = useBackend();
  const systemConfig = useSystemConfig();
  const { resolveUser } = useUserResolution();
  const { handleGridSearchError } = useGridSearchErrorHandler('projects');
  const { isAuthorized } = usePrivileges();
  const readOp: Operation = 'read';
  const canViewSampleRequestCounts = isAuthorized(PrivilegeEnum.SampleRequest, readOp);
  const canViewShippingCounts = isAuthorized(PrivilegeEnum.Shipping, readOp);
  /** Matches ProjectApiController search: NbSamplesDue / TotalNbSamples require SamplesDue. */
  const canViewSamplesDueInfo = isAuthorized(PrivilegeEnum.SamplesDue, readOp);

  // Use optionsData passed from parent
  const finalOptions = optionsData || {};
  const finalOptionsLoading = optionsLoading || false;

  // Use props if provided (for selection dialogs), otherwise use store
  const storeState = useProjectFilterStore();
  const filters = propFilters ?? storeState.filters;
  const paginationModel = propPaginationModel ?? storeState.paginationModel;
  const setPaginationModel = onPaginationModelChange ?? storeState.setPaginationModel;
  const setIsSearching = storeState.setIsSearching;
  const [rows, setRows] = useState<ProjectRowType[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>(loadStoredProjectSortModel());

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(loadStoredColumnVisibility());

  const [density, setDensity] = useState<GridDensity>(propDensity || 'standard');

  const handleColumnVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    const enforced = enforceProjectGridNonHideableColumns(newModel);
    setColumnVisibilityModel(enforced);
    scopedLocalStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(enforced));
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel);
    scopedLocalStorage.setItem(PROJECT_SORT_MODEL_STORAGE_KEY, JSON.stringify(newModel));
  };

  // Only memoize the material map since it's larger and more complex
  const materialMap = useMemo(
    () =>
      new Map<number, string>(
        finalOptions.materials
          ?.filter((m) => m.id !== undefined && m.name !== undefined)
          .map((m) => [m.id as number, m.name as string])
      ),
    [finalOptions.materials]
  );

  // Fetch data when filters, pagination, sort, or options change
  useEffect(() => {
    // Don't fetch if options are still loading
    if (finalOptionsLoading) {
      log.debug('[ProjectGrid] Options still loading, skipping fetch');
      return;
    }

    // Check if we have the minimum required options loaded
    const hasMinimumOptions = finalOptions.projectStatus && finalOptions.projectType;

    if (!hasMinimumOptions) {
      log.debug('[ProjectGrid] Missing minimum required options, skipping fetch:', {
        finalOptionsLoading,
        hasMinimumOptions,
        projectStatus: !!finalOptions.projectStatus,
        projectType: !!finalOptions.projectType,
      });
      return;
    }

    const fetchProjects = async () => {
      const userInitiatedSearch = consumeNextSearchUserInitiated();
      log.debug('[ProjectGridCore] fetchProjects called');
      try {
        setIsSearching(true);
        setLoading(true);
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;
        const draw = paginationModel.page + 1;
        const sortColumn = sortModel[0]?.field || 'projectLabel';
        const sortOrder = sortModel[0]?.sort || 'asc';
        const criteria = {
          ...filters,
          status: (filters.status?.trim() || undefined) as ProjectSearchStatusDto | undefined,
          state: filters.state?.trim() || undefined,
          institution: filters.institution?.trim() || undefined,
        };

        const projectList = await backend.project.search(sortColumn, sortOrder, start, length, draw, criteria);

        const formattedRows: ProjectRowType[] = projectList.data.map((project: any) => {
          const user = project.collaborators?.find((c: any) => c.id === project.user);
          const facultyNames = formatCollaboratorNamesForRoleColumns(project.collaborators, project.faculty);
          const staffNames = formatCollaboratorNamesForRoleColumns(project.collaborators, project.staff);

          return {
            id: project.id!,
            projectLabel: project.projectLabel || project.id?.toString() || '-',
            type: project.type ? getProjectTypeLabel(project.type, finalOptions.projectType || []) : '-',
            title: project.title || '-',
            startDate: project.startDate ? new Date(project.startDate).toLocaleDateString() : '-',
            endDate: project.endDate ? new Date(project.endDate).toLocaleDateString() : '-',
            projectStatus: project.projectStatus
              ? getProjectStatusLabel(project.projectStatus, finalOptions.projectStatus || []) || project.projectStatus
              : '-',
            user: user ? user.firstName + ' ' + user.lastName : '-',
            institutionType: project.institutionType?.trim() || '-',
            piInstitution: project.piInstitution?.trim() || '-',
            piState: project.piState?.trim() || undefined,
            faculty: facultyNames,
            staff: staffNames,
            totalNbSamplesRequested: project.totalNbSamplesRequested,
            nbSamplesShipped: project.nbSamplesShipped,
            totalNbSamples: project.totalNbSamples,
            nbSamplesDue: project.nbSamplesDue,
            lastChangedDate: project.lastChangedDate ? new Date(project.lastChangedDate).toLocaleDateString() : '-',
            lastChangedBy: project.lastChangedByUser
              ? resolveUser(project.lastChangedByUser) || project.lastChangedByUser
              : '-',
            materials: formatProjectMaterialsCell(project, materialMap),
            techniques: formatProjectTechniquesCell(project, finalOptions.synthesisTechniques || []),
          };
        });

        setRows(formattedRows ?? []);
        setTotalRows(projectList?.recordsFiltered ?? 0);
      } catch (error) {
        log.error('[ProjectGridCore] Error fetching projects:', error);
        handleGridSearchError(error, { userInitiatedSearch });
      } finally {
        setLoading(false);
        setIsSearching(false);
      }
    };

    log.debug('[ProjectGrid] All options loaded, fetching data');
    fetchProjects();
  }, [
    backend.project,
    filters,
    paginationModel,
    sortModel,
    resolveUser,
    finalOptionsLoading,
    finalOptions.projectStatus,
    finalOptions.projectType,
    finalOptions.synthesisTechniques,
    materialMap,
    setIsSearching,
    handleGridSearchError,
  ]);

  // Handle project selection with full data
  const handleSelectionChangeWithData = (newSelectionModel: GridRowSelectionModel) => {
    // Call the original onSelectionChange if provided
    onSelectionChange?.(newSelectionModel);

    // Handle project selection with full data
    if (onProjectSelect && newSelectionModel.ids.size === 1) {
      const selectedId = Array.from(newSelectionModel.ids)[0];
      const selectedProject = rows.find((row) => row.id === selectedId);
      if (selectedProject) {
        onProjectSelect(selectedProject);
      }
    }

    if (onProjectsSelect && newSelectionModel.ids.size > 0) {
      const selectedProjects = rows.filter((row) => newSelectionModel.ids.has(row.id));
      onProjectsSelect(selectedProjects);
    }
  };

  const columnGroupingModel: GridColumnGroupingModel = useMemo(
    () => [
      {
        groupId: 'project',
        headerName: 'Project',
        children: [
          { field: 'projectLabel' },
          { field: 'title' },
          { field: 'user' },
          { field: 'techniques' },
          { field: 'institutionType' },
        ],
      },
      {
        groupId: 'projectDetails',
        headerName: 'Project Details',
        children: [
          { field: 'projectStatus' },
          { field: 'faculty' },
          { field: 'staff' },
          { field: 'materials' },
          { field: 'piInstitution' },
          { field: 'type' },
          { field: 'piState' },
          { field: 'startDate' },
          { field: 'endDate' },
        ],
      },
      {
        groupId: 'samples',
        headerName: 'Samples',
        children: [
          { field: 'totalNbSamplesRequested' },
          { field: 'nbSamplesShipped' },
          { field: 'totalNbSamples' },
          { field: 'nbSamplesDue' },
        ],
      },
      {
        groupId: 'lastChanged',
        headerName: 'Last Changed',
        children: [{ field: 'lastChangedDate' }, { field: 'lastChangedBy' }],
      },
    ],
    []
  );

  const defaultColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'projectLabel',
        headerName: 'ID',
        flex: 1,
        minWidth: 150,
        hideable: false,
        renderCell: showLinks
          ? (params) => (
              <Link href={`${systemConfig?.basePath}/projects/${encodeURIComponent(params.value)}`}>
                {params.value}
              </Link>
            )
          : undefined,
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 200,
        hideable: false,
        renderCell: (params) => {
          const cleanTitle = removeLeadingTrailingBrTags(params.value || '-');

          return (
            <Tooltip
              title={<div dangerouslySetInnerHTML={{ __html: cleanTitle }} style={{ maxWidth: '400px' }} />}
              placement="top-start"
              arrow
            >
              <div
                dangerouslySetInnerHTML={{ __html: cleanTitle }}
                style={{
                  width: '100%',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.2em',
                  maxHeight: '5em',
                  alignItems: 'flex-start', // Align to top
                  alignSelf: 'flex-start', // Align to top
                }}
                className="project-title-cell"
              />
            </Tooltip>
          );
        },
      },
      { field: 'user', headerName: 'PI', flex: 1, minWidth: 150, sortable: false, hideable: false },
      { field: 'techniques', headerName: 'Techniques', flex: 1, minWidth: 150 },
      {
        field: 'institutionType',
        headerName: 'Institution type',
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
      { field: 'projectStatus', headerName: 'Status', flex: 1, minWidth: 120 },
      // sortable is set as per the CanBeSortedInDatabase method on the backend
      { field: 'faculty', headerName: 'Faculty', flex: 1, minWidth: 150, sortable: false },
      { field: 'staff', headerName: 'Staff', flex: 1, minWidth: 150, sortable: false },
      { field: 'materials', headerName: 'Materials', flex: 1, minWidth: 150 },
      {
        field: 'piInstitution',
        headerName: 'PI institution',
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      { field: 'type', headerName: 'Type', flex: 1, minWidth: 120 },
      {
        field: 'piState',
        headerName: 'PI state',
        width: 200,
        sortable: false,
        valueGetter: (_v, row) => formatUsJurisdictionForDisplay(row.piState),
      },
      { field: 'startDate', headerName: 'Start Date', flex: 1, minWidth: 120 },
      { field: 'endDate', headerName: 'End Date', flex: 1, minWidth: 120 },
      {
        field: 'totalNbSamplesRequested',
        headerName: 'Samples Requested',
        flex: 1,
        minWidth: 150,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => formatNullableCount(params.row.totalNbSamplesRequested),
      },
      {
        field: 'nbSamplesShipped',
        headerName: 'Samples Shipped',
        flex: 1,
        minWidth: 150,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => formatNullableCount(params.row.nbSamplesShipped),
      },
      {
        field: 'totalNbSamples',
        headerName: 'Total Samples',
        flex: 1,
        minWidth: 150,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => formatNullableCount(params.row.totalNbSamples),
      },
      {
        field: 'nbSamplesDue',
        headerName: 'Samples Due',
        flex: 1,
        minWidth: 150,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => formatNullableCount(params.row.nbSamplesDue),
      },
      { field: 'lastChangedDate', headerName: 'Last Changed', flex: 1, minWidth: 150 },
      { field: 'lastChangedBy', headerName: 'Changed By', flex: 1, minWidth: 150 },
    ],
    [showLinks, systemConfig?.basePath]
  );

  const columns = customColumns || defaultColumns;

  // Auto-hide Faculty column when it's empty for all returned projects.
  // Note: rows currently use "-" as the placeholder for empty values.
  // We intentionally do NOT persist this auto-hide decision to localStorage.
  const hasAnyFacultyValue = useMemo(() => {
    return rows.some((r) => {
      const v = (r.faculty ?? '').toString().trim();
      return v !== '' && v !== '-';
    });
  }, [rows]);

  /**
   * Applies non-persisted rules: hide Faculty when empty, and hide sample metric columns when
   * the user lacks the same privileges the search API uses to populate those fields.
   */
  const effectiveColumnVisibilityModel = useMemo(() => {
    let m: GridColumnVisibilityModel = { ...columnVisibilityModel };
    if (!hasAnyFacultyValue) {
      m = { ...m, faculty: false };
    }
    if (!canViewSampleRequestCounts) {
      m = { ...m, totalNbSamplesRequested: false };
    }
    if (!canViewShippingCounts) {
      m = { ...m, nbSamplesShipped: false };
    }
    if (!canViewSamplesDueInfo) {
      m = { ...m, totalNbSamples: false, nbSamplesDue: false };
    }
    return m;
  }, [
    columnVisibilityModel,
    hasAnyFacultyValue,
    canViewSampleRequestCounts,
    canViewShippingCounts,
    canViewSamplesDueInfo,
  ]);

  const noRowsOverlay = useMemo(
    () =>
      function ProjectGridNoRowsOverlay() {
        return <ProjectSearchNoRowsOverlay ownProjectsOnly={!!filters.ownProjectsOnly} />;
      },
    [filters.ownProjectsOnly]
  );

  const defaultProjectToolbar = useCallback(
    function DefaultProjectToolbar() {
      return (
        <ProjectGridToolbar
          canViewSampleRequestCounts={canViewSampleRequestCounts}
          canViewShippingCounts={canViewShippingCounts}
          canViewSamplesDueInfo={canViewSamplesDueInfo}
        />
      );
    },
    [canViewSampleRequestCounts, canViewShippingCounts, canViewSamplesDueInfo]
  );

  return (
    <DataGridPro
      rows={rows}
      columns={columns}
      columnGroupingModel={columnGroupingModel}
      loading={loading || finalOptionsLoading}
      rowCount={totalRows}
      columnVisibilityModel={effectiveColumnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityChange}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[25, 50, 100]}
      disableRowSelectionOnClick={!enableSelection}
      disableColumnMenu={disableColumnMenu}
      showToolbar={showToolbar}
      density={density}
      onDensityChange={(newDensity) => setDensity(newDensity)}
      slots={{
        toolbar: showToolbar ? toolbarSlot || defaultProjectToolbar : undefined,
        noRowsOverlay,
      }}
      getRowHeight={() => 'auto'}
      checkboxSelection={checkboxSelection}
      onRowSelectionModelChange={handleSelectionChangeWithData}
      rowSelectionModel={selectionModel}
      pinnedColumns={{ left: ['projectLabel'] }}
      sx={{
        ...dataGridStyle,
      }}
    />
  );
};
